import { useState, useCallback, useRef } from 'react';
import type { EntityFormConfig } from '@components/integrated/entity-form';
import { documentsService, type VeosDocumentRow } from '../../api/documentsService';

// ---------------------------------------------------------------------------
// Shared, non-component primitives for the "Classement + Libellé" autosave form
// (see DocumentClassementForm.tsx for the presentational + registered parts).
//
// Kept in a JSX-free module so the components file can export only components
// (react-refresh/only-export-components). The two inputs let a user
// re-categorise (`Classement`) and rename (`Libellé`) a filed document via
// `EXT_MODIF_GED`; the exact same pair is reused for PDFs/images
// (`DocumentPreview`) and — per mahhp 22268 — emails (`EmailClassementForm`).
// Per mahhp 22843 the same two keys also drive the "put in GED" controls of an
// email attachment row (`buildAttachmentGedConfig`).
// ---------------------------------------------------------------------------

const SAVE_ERROR = "Erreur lors de l'enregistrement du classement.";

/**
 * Stable field keys for the two autosave inputs — the rule contract a
 * tenant-scoped `renameField` / `hideField` / `reorderFields` /
 * `setFieldPlaceholder` targets. Intentionally separate from the display labels
 * (which live in {@link buildClassementConfig} and are overridable). Never
 * rename a key.
 */
export const CLASSEMENT_FIELD = {
  classement: 'classement',
  libelle: 'libelle',
} as const;

/**
 * Declarative {@link EntityFormConfig} for the two autosave inputs. Every
 * input's French `label`, `placeholder`, `order` (classement 1, libelle 2 — the
 * historic left-to-right layout) and `visible` lives here — off the JSX — so an
 * admin can rename / hide / reorder / replace-placeholder each input per tenant
 * via the rule pipeline. The `id` is passed in so each host publishes under its
 * own component id. The controls stay bespoke renderers (a side-effecting
 * `<Select>` + an autosave `<TextField>`); `endpoint` is a never-invoked
 * placeholder that satisfies the required shape.
 */
export function buildClassementConfig(id: string): EntityFormConfig {
  return {
    id,
    endpoint: {
      erpEndpoint: `${id}.noop`,
      method: 'POST',
    },
    fields: {
      [CLASSEMENT_FIELD.classement]: {
        type: 'select',
        label: 'Classement',
        placeholder: 'Sans catégorie',
        order: 1,
        visible: true,
      },
      [CLASSEMENT_FIELD.libelle]: {
        type: 'text',
        label: 'Libellé',
        placeholder: 'Nom du document',
        order: 2,
        visible: true,
      },
    },
  };
}

/**
 * Config id of the per-attachment GED controls built by
 * {@link buildAttachmentGedConfig}. They are a surface of the registered
 * `claimflow.emailView` (which merges its `_layoutOverrides` into them and
 * publishes them) — not a separately-registered component.
 */
export const ATTACHMENT_GED_CONFIG_ID = 'claimflow.emailView.attachmentGed';

/**
 * Declarative {@link EntityFormConfig} for the "put in GED" controls of an email
 * attachment row (`EmailAttachmentRow`, mahhp 22843). Reuses the SAME stable
 * keys as {@link buildClassementConfig} ({@link CLASSEMENT_FIELD}):
 * `classement` is the GED category select (sent as `document-create` `type`),
 * `libelle` the document-name input — preloaded with the file name without its
 * extension and sent as `label`. Labels / placeholders / order / visible live
 * here so an admin can retext, hide or reorder each control per tenant; the
 * labels are not rendered visibly (the row has no room — see the mockup) but
 * are what the layout editor lists. The "Valider" button is config-sourced via
 * `actions.submit` (label / visible) so `setActionLabel` / `hideAction` rules
 * drive it. The controls stay bespoke renderers; `endpoint` is the same
 * never-invoked placeholder as {@link buildClassementConfig}.
 */
export function buildAttachmentGedConfig(id: string): EntityFormConfig {
  return {
    id,
    endpoint: {
      erpEndpoint: `${id}.noop`,
      method: 'POST',
    },
    fields: {
      [CLASSEMENT_FIELD.classement]: {
        type: 'select',
        label: 'Classement',
        placeholder: 'Mettre en GED',
        order: 1,
        visible: true,
      },
      [CLASSEMENT_FIELD.libelle]: {
        type: 'text',
        label: 'Libellé',
        placeholder: 'Nom du document',
        order: 2,
        visible: true,
      },
    },
    actions: {
      submit: { label: 'Valider', visible: true },
    },
  };
}

export interface DocumentClassementState {
  cdDs: string;
  libelle: string;
  isSaving: boolean;
  saveError: string | null;
  setLibelle: (value: string) => void;
  /** Change the classement and immediately persist (the select commits on change). */
  onSelectChange: (next: string) => void;
  /** Persist the current values (used on the libellé input's blur). */
  onLibelleBlur: () => void;
}

/**
 * State + autosave persistence for the classement/libellé pair. Seeds from the
 * row (`TYPE_ADOC` / `LIB_ADOC`); the host remounts per selected row so no reset
 * effect is needed. `type` is the `EXT_MODIF_GED` `TYPE` — `row.API`
 * (`"DOC"` | `"EMAIL"`) — so the same hook drives documents and emails. A save
 * only fires on a real change (tracked via `savedRef`) to avoid redundant
 * round-trips + list refreshes.
 */
export function useDocumentClassement(
  row: VeosDocumentRow,
  type: string,
  onSaved?: () => void,
): DocumentClassementState {
  const [cdDs, setCdDs] = useState(row.TYPE_ADOC);
  const [libelle, setLibelle] = useState(row.LIB_ADOC);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const savedRef = useRef({ cdDs: row.TYPE_ADOC, libelle: row.LIB_ADOC });

  const persist = useCallback(
    async (nextCdDs: string, nextLibelle: string) => {
      if (nextCdDs === '') return;
      if (nextCdDs === savedRef.current.cdDs && nextLibelle === savedRef.current.libelle) {
        return;
      }
      setIsSaving(true);
      setSaveError(null);
      try {
        await documentsService.updateGed({
          TYPE: type,
          ID: String(row.ID_ADOC),
          CD_DS: nextCdDs,
          LIB: nextLibelle,
        });
        savedRef.current = { cdDs: nextCdDs, libelle: nextLibelle };
        onSaved?.();
      } catch {
        setSaveError(SAVE_ERROR);
      } finally {
        setIsSaving(false);
      }
    },
    [row.ID_ADOC, type, onSaved],
  );

  const onSelectChange = useCallback(
    (next: string) => {
      setCdDs(next);
      void persist(next, libelle);
    },
    [persist, libelle],
  );

  const onLibelleBlur = useCallback(() => {
    void persist(cdDs, libelle);
  }, [persist, cdDs, libelle]);

  return { cdDs, libelle, isSaving, saveError, setLibelle, onSelectChange, onLibelleBlur };
}
